import { useState } from "react";
import { Mail, Loader2, Copy, Check, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const LEAVE_TYPES = ["Sick Leave", "Casual Leave", "Vacation", "Personal Leave", "Family Emergency", "Work From Home", "Bereavement Leave", "Maternity/Paternity Leave"];
const TONES = [
  { value: "formal", label: "Formal" },
  { value: "friendly", label: "Friendly" },
  { value: "urgent", label: "Urgent" },
];

export default function LeaveGenerator() {
  const [leaveType, setLeaveType] = useState("Sick Leave");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [managerName, setManagerName] = useState("");
  const [tone, setTone] = useState("formal");
  const [application, setApplication] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    setError("");
    setApplication("");

    try {
      const res = await axios.post(`${API}/leave/generate`, {
        leave_type: leaveType,
        start_date: startDate || "Tomorrow",
        end_date: endDate || startDate || "Tomorrow",
        reason: reason.trim(),
        employee_name: employeeName,
        manager_name: managerName,
        tone,
      });
      setApplication(res.data.application);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(application);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setReason("");
    setApplication("");
    setError("");
    setEmployeeName("");
    setManagerName("");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#D97706]" />
            </div>
            <span className="label-mono">Leave Generator</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="leave-page-title">
            Leave application generator
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Fill in the details and get a professional, ready-to-send leave application email in seconds.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-2">Leave Type</label>
                <Select value={leaveType} onValueChange={setLeaveType}>
                  <SelectTrigger className="bg-slate-50 border-transparent focus:border-[#D97706] h-11 rounded-md" data-testid="leave-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-mono block mb-2">Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="bg-slate-50 border-transparent focus:border-[#D97706] h-11 rounded-md" data-testid="tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-2">From Date</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-slate-50 border-transparent focus:border-[#D97706] h-11 rounded-md" data-testid="start-date-input" />
              </div>
              <div>
                <label className="label-mono block mb-2">To Date</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-slate-50 border-transparent focus:border-[#D97706] h-11 rounded-md" data-testid="end-date-input" />
              </div>
            </div>

            <div>
              <label className="label-mono block mb-2">Reason</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Brief reason for leave..."
                className="min-h-[80px] text-sm bg-slate-50 border-transparent focus:border-[#D97706] focus:bg-white rounded-md resize-none"
                data-testid="reason-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-2">Your Name (optional)</label>
                <Input value={employeeName} onChange={(e) => setEmployeeName(e.target.value)} placeholder="John Doe" className="bg-slate-50 border-transparent h-11 rounded-md" data-testid="employee-name-input" />
              </div>
              <div>
                <label className="label-mono block mb-2">Manager (optional)</label>
                <Input value={managerName} onChange={(e) => setManagerName(e.target.value)} placeholder="Jane Smith" className="bg-slate-50 border-transparent h-11 rounded-md" data-testid="manager-name-input" />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleGenerate} disabled={loading || !reason.trim()} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-md px-6 h-11 font-medium btn-press" data-testid="generate-leave-button">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Mail className="w-4 h-4 mr-2" />Generate Application</>}
              </Button>
              {(reason || application) && (
                <Button variant="outline" onClick={handleReset} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press" data-testid="leave-reset-button">
                  <RotateCcw className="w-4 h-4 mr-1.5" />Reset
                </Button>
              )}
            </div>
            {error && <p className="text-sm text-red-500" data-testid="leave-error">{error}</p>}

            <AdUnit format="horizontal" />
          </div>

          <div className="lg:col-span-7">
            <div className="border border-slate-200 rounded-lg bg-white min-h-[400px]">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <span className="label-mono">Generated Application</span>
                {application && (
                  <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#D97706] transition-colors" data-testid="copy-leave-button">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
              </div>
              <div className="p-5" data-testid="leave-output">
                {loading && (
                  <div className="flex items-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Writing your application...</span>
                  </div>
                )}
                {!loading && !application && (
                  <div className="text-sm text-slate-400 flex flex-col items-center justify-center py-16">
                    <Mail className="w-10 h-10 text-slate-300 mb-3" />
                    <p>Fill in the details and click "Generate Application"</p>
                  </div>
                )}
                {application && (
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{application}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
